import { dateUtil, numberUtil } from "@xtnd/web/shared";
import { Appeal } from '../../types';
import { Card, Icon, Skeleton } from '@repo/ui';

interface SummaryItemProps {
  label: string;
  value?: React.ReactNode;
  highlight?: boolean;
  type?: 'text' | 'number';
}

const SummaryItem: React.FC<SummaryItemProps> = ({
  label,
  value,
  highlight = false,
  type = 'text',
}) => (
  <div className="text-sm">
    <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
      <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
        {label}
      </span>
      <span
        className={`ml-auto text-right ${type == 'number' && 'font-mono'} break-words max-w-[80%] ${
          highlight ? 'font-semibold' : ''
        }`}
      >
        {value ?? '-'}
      </span>
    </div>
  </div>
);

export const AppealSummarySkeleton = () => {
  return (
    <Card className="grid grid-cols-1 gap-x-8 gap-y-3 animate-pulse">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <span className="h-5 w-5 rounded bg-gray-200 dark:bg-xtnd-dark-600" />
        <span className="h-5 w-56 rounded bg-gray-200 dark:bg-xtnd-dark-600" />
      </div>

      {/* Summary rows */}
      {[...Array(8)].map((_, item) => (
        <Skeleton key={item} className="h-4 w-24" />
      ))}
    </Card>
  );
};

export const AppealSummary = ({ basicInformation }: { basicInformation: Partial<Appeal> }) => {
  if (!basicInformation) return null;
  const {
    matterDisplayNumber,
    clientName,
    invoiceNumber,
    invoiceDate,
    totalAmount,
    amountDue,
    paid,
    creditsIssued,
    responsibleAttorneyName,
  } = basicInformation;
  return (
    <Card
      className="grid grid-cols-1 gap-x-8 gap-y-3 animate-fadeInRight"
      style={{ animationDelay: '0.15s' }}
    >
      {/* Header – full width */}
      <h3 className="col-span-full text-lg font-bold mb-2 flex items-center text-xtnd-dark dark:text-xtnd-light">
        <Icon
          name="HiInformationCircle"
          className="w-5 h-5 mr-2 text-xtnd-dark dark:text-xtnd-light"
        />
        Basic Information
      </h3>

      <SummaryItem label="Matter" value={matterDisplayNumber} />
      <SummaryItem label="Responsible Attorney" value={responsibleAttorneyName} />
      <SummaryItem label="Client" value={clientName} />
      <SummaryItem label="Invoice #" value={invoiceNumber} />
      <SummaryItem
        label="Invoice Date"
        value={
          invoiceDate ? dateUtil.formatDate(invoiceDate, dateUtil.DateFormat.US) : '-'
        }
      />
      <SummaryItem
        highlight
        label="Original Invoice Amount"
        value={numberUtil.formatCurrency(totalAmount || 0)}
        type="number"
      />
      <SummaryItem
        label="Paid"
        value={numberUtil.formatCurrency(paid || 0)}
        highlight
        type="number"
      />
      <SummaryItem
        label="Credits Issued"
        value={numberUtil.formatCurrency(creditsIssued || 0)}
        highlight
        type="number"
      />
      <SummaryItem
        label="Amount Due"
        value={numberUtil.formatCurrency(amountDue || 0)}
        highlight
        type="number"
      />
    </Card>
  );
};
  